import { AdjustBlur, AdjustCrop, AdjustDocument, AdjustLookId, AdjustResize, AdjustShape, AdjustSticker, AdjustmentKey, DEFAULT_ADJUSTMENTS, DEFAULT_CROP, DEFAULT_RESIZE, createAdjustDocument } from './adjust.types'

import { EditionAction } from '../photoStudioTypes'

/** Every edit is one of these; `applyAdjustAction` is pure and returns the same object when nothing changes. */
export type AdjustAction =
  | { type: 'setAdjustment', key: AdjustmentKey, value: number }
  | { type: 'resetAdjustment', key: AdjustmentKey }
  | { type: 'resetAdjustments' }
  | { type: 'setLook', id: AdjustLookId }
  | { type: 'setLookIntensity', intensity: number }
  | { type: 'setCrop', crop: Partial<AdjustCrop> }
  | { type: 'resetCrop' }
  | { type: 'setResize', resize: Partial<AdjustResize> }
  | { type: 'addBlur', blur: AdjustBlur }
  | { type: 'updateBlur', id: string, patch: Partial<AdjustBlur> }
  | { type: 'clearBlurs' }
  | { type: 'addShape', shape: AdjustShape }
  | { type: 'updateShape', id: string, patch: Partial<AdjustShape> }
  | { type: 'clearShapes' }
  | { type: 'addSticker', sticker: AdjustSticker }
  | { type: 'updateSticker', id: string, patch: Partial<AdjustSticker> }
  | { type: 'removeSticker', id: string }

const updateById = <T extends { id: string }>(items: T[], id: string, patch: Partial<T>): T[] => {
  const index = items.findIndex(item => item.id === id)
  if (index === -1) return items
  const next = items.slice()
  next[index] = { ...items[index], ...patch }
  return next
}

export const applyAdjustAction = (doc: AdjustDocument, action: AdjustAction): AdjustDocument => {
  switch (action.type) {
    case 'setAdjustment':
      if (doc.adjustments[action.key] === action.value) return doc
      return { ...doc, adjustments: { ...doc.adjustments, [action.key]: action.value } }
    case 'resetAdjustment':
      if (doc.adjustments[action.key] === DEFAULT_ADJUSTMENTS[action.key]) return doc
      return { ...doc, adjustments: { ...doc.adjustments, [action.key]: DEFAULT_ADJUSTMENTS[action.key] } }
    case 'resetAdjustments':
      return { ...doc, adjustments: { ...DEFAULT_ADJUSTMENTS } }
    case 'setLook':
      if (doc.look.id === action.id) return doc
      return { ...doc, look: { id: action.id, intensity: 100 } }
    case 'setLookIntensity':
      if (doc.look.intensity === action.intensity) return doc
      return { ...doc, look: { ...doc.look, intensity: action.intensity } }
    case 'setCrop': {
      const crop = { ...doc.crop, ...action.crop }
      // A new ratio or orientation starts from its largest centred frame
      const isNewRatio = (action.crop.aspect !== undefined && action.crop.aspect !== doc.crop.aspect) ||
        (action.crop.portrait !== undefined && action.crop.portrait !== doc.crop.portrait)
      if (isNewRatio && action.crop.rect === undefined) crop.rect = null
      return { ...doc, crop, resize: isNewRatio ? { ...DEFAULT_RESIZE } : doc.resize }
    }
    case 'resetCrop':
      return { ...doc, crop: { ...DEFAULT_CROP }, resize: { ...DEFAULT_RESIZE } }
    case 'setResize':
      return { ...doc, resize: { ...doc.resize, ...action.resize } }
    case 'addBlur':
      return { ...doc, blurs: [...doc.blurs, action.blur] }
    case 'updateBlur':
      return { ...doc, blurs: updateById(doc.blurs, action.id, action.patch) }
    case 'clearBlurs':
      return doc.blurs.length ? { ...doc, blurs: [] } : doc
    case 'addShape':
      return { ...doc, shapes: [...doc.shapes, action.shape] }
    case 'updateShape':
      return { ...doc, shapes: updateById(doc.shapes, action.id, action.patch) }
    case 'clearShapes':
      return doc.shapes.length ? { ...doc, shapes: [] } : doc
    case 'addSticker':
      return { ...doc, stickers: [...doc.stickers, action.sticker] }
    case 'updateSticker':
      return { ...doc, stickers: updateById(doc.stickers, action.id, action.patch) }
    case 'removeSticker':
      return { ...doc, stickers: doc.stickers.filter(sticker => sticker.id !== action.id) }
    default:
      return doc
  }
}

export const ADJUST_HISTORY_LIMIT = 50
/** Slider ticks with the same key closer than this become one undo step */
export const ADJUST_HISTORY_COALESCE_MS = 1000
/** Keys starting with this coalesce until `commit` (a pointer gesture: a stroke, a crop drag, a sticker move) */
export const GESTURE_KEY_PREFIX = 'gesture:'

export interface AdjustHistory {
  past: AdjustDocument[]
  present: AdjustDocument
  future: AdjustDocument[]
  lastKey: string | null
  lastAt: number
}

export type AdjustHistoryAction =
  | { type: 'apply', action: AdjustAction, historyKey?: string, now: number }
  | { type: 'commit' }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'reset' }

export const createAdjustHistory = (): AdjustHistory => ({
  past: [],
  present: createAdjustDocument(),
  future: [],
  lastKey: null,
  lastAt: 0,
})

export const adjustHistoryReducer = (state: AdjustHistory, event: AdjustHistoryAction): AdjustHistory => {
  switch (event.type) {
    case 'apply': {
      const next = applyAdjustAction(state.present, event.action)
      if (next === state.present) return state
      const key = event.historyKey ?? null
      const isCoalesced = key !== null && key === state.lastKey &&
        (key.startsWith(GESTURE_KEY_PREFIX) || event.now - state.lastAt < ADJUST_HISTORY_COALESCE_MS)
      if (isCoalesced) return { ...state, present: next, future: [], lastAt: event.now }
      return {
        past: [...state.past, state.present].slice(-ADJUST_HISTORY_LIMIT),
        present: next,
        future: [],
        lastKey: key,
        lastAt: event.now,
      }
    }
    case 'commit':
      return state.lastKey === null ? state : { ...state, lastKey: null }
    case 'undo': {
      if (!state.past.length) return state
      const previous = state.past[state.past.length - 1]
      return { past: state.past.slice(0, -1), present: previous, future: [state.present, ...state.future], lastKey: null, lastAt: 0 }
    }
    case 'redo': {
      if (!state.future.length) return state
      const [next, ...rest] = state.future
      return { past: [...state.past, state.present], present: next, future: rest, lastKey: null, lastAt: 0 }
    }
    case 'reset':
      return createAdjustHistory()
    default:
      return state
  }
}

const isAdjustmentsChanged = (doc: AdjustDocument) =>
  (Object.keys(DEFAULT_ADJUSTMENTS) as AdjustmentKey[]).some(key => doc.adjustments[key] !== DEFAULT_ADJUSTMENTS[key])

const isLookApplied = (doc: AdjustDocument) => doc.look.id !== 'original' && doc.look.intensity > 0

const isRotated = (crop: AdjustCrop) => crop.straighten !== 0 || ((crop.quarterTurns % 4) + 4) % 4 !== 0

/** A ratio other than the photo's own, or a frame that was dragged */
const isCropped = (crop: AdjustCrop) => crop.rect !== null || (crop.aspect !== 'custom' && crop.aspect !== 'original')

const isResized = (resize: AdjustResize) => resize.scale < 1 || (!resize.lock && resize.height !== null)

export const hasAdjustEdits = (doc: AdjustDocument): boolean =>
  isAdjustmentsChanged(doc) ||
  isLookApplied(doc) ||
  isCropped(doc.crop) ||
  isRotated(doc.crop) ||
  doc.crop.flipX ||
  doc.crop.flipY ||
  isResized(doc.resize) ||
  doc.blurs.length > 0 ||
  doc.shapes.length > 0 ||
  doc.stickers.length > 0

/** The edition actions of a document, in the order Pintura's `getUsedEditionActions` reports them */
export const getAdjustEditionActions = (doc: AdjustDocument): EditionAction[] => {
  const actions: EditionAction[] = []
  if (isCropped(doc.crop) || isRotated(doc.crop) || doc.crop.flipX || doc.crop.flipY) actions.push(EditionAction.CROP)
  if (isAdjustmentsChanged(doc)) actions.push(EditionAction.FINE_TUNE)
  if (isLookApplied(doc)) actions.push(EditionAction.FILTER)
  if (doc.shapes.length > 0) actions.push(EditionAction.ANNOTATE)
  if (doc.stickers.length > 0) actions.push(EditionAction.STICKER)
  if (doc.blurs.length > 0) actions.push(EditionAction.BLUR)
  if (isResized(doc.resize)) actions.push(EditionAction.RESIZE)
  return actions
}

/** The booleans PHOTO_STUDIO_SAVE_IMAGE has always sent for an Adjust save (derived from Pintura's changes before) */
export const getAdjustSaveAnalyticsFlags = (doc: AdjustDocument): Record<string, boolean> => ({
  Crop: isCropped(doc.crop),
  Rotation: isRotated(doc.crop),
  FlipX: doc.crop.flipX,
  FlipY: doc.crop.flipY,
  FineTune: isAdjustmentsChanged(doc),
  Filter: isLookApplied(doc),
  Annotate: doc.shapes.length > 0,
  Sticker: doc.stickers.length > 0,
  Redact: doc.blurs.length > 0,
  Frame: false,
  Resize: isResized(doc.resize),
})

/** Rough size of the edit, for the "Processing your edits" progress estimate when leaving Adjust */
export const getAdjustEditCount = (doc: AdjustDocument): number => getAdjustEditionActions(doc).length + doc.blurs.length + doc.shapes.length + doc.stickers.length
